import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Plus, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { FieldSelectFit } from "@/components/trade-filter-selects";
import { compileStrategyRdRule, type StrategyRdRuleComposer } from "../../../services/strategyRdApi";
import type {
  StrategyRdEntryRuleSpec,
  StrategyRdRuleExpr,
  StrategyRdRuleRole,
  StrategyRdRuleVocabulary,
} from "../../../types/strategyRd";
import {
  STRATEGY_RD_COMBINE_LABELS,
  STRATEGY_RD_COMPARE_LABELS,
  STRATEGY_RD_COMPARE_OPS,
  STRATEGY_RD_DIST_UNIT_LABELS,
  STRATEGY_RD_OFFSET_DIR_LABELS,
  STRATEGY_RD_PRICE_TESTS,
  STRATEGY_RD_PRICE_TEST_LABELS,
  STRATEGY_RD_RULE_TIME_ZONES,
  strategyRdCondSentenceFromJson,
  strategyRdCondSentenceToJson,
  strategyRdDefaultCondItem,
  strategyRdDefaultPriceForm,
  strategyRdPriceSentenceFromJson,
  strategyRdPriceSentenceToJson,
  strategyRdRuleDistUnits,
  strategyRdRuleExamples,
  strategyRdRulePriceValues,
  strategyRdRuleSourceLabel,
  strategyRdRuleSourcesForRole,
  strategyRdRuleValueLabel,
  strategyRdRuleZoneSources,
  type StrategyRdCondItem,
  type StrategyRdCondSentence,
  type StrategyRdCondTest,
  type StrategyRdPriceForm,
  type StrategyRdPriceSentence,
  type StrategyRdRuleCombineOp,
  type StrategyRdRuleDistanceUnit,
  type StrategyRdRuleOffsetDir,
  type StrategyRdRuleOperand,
} from "../../../utils/strategyRdRuleEditor";
import { strategyRdBrandActionButtonClassName, strategyRdMetaClassName } from "./strategyRdCardShell";

/** Compile state of one rule field — `error` / `checking` block Apply. */
export type StrategyRdRuleStatus = {
  state: "idle" | "checking" | "ok" | "error";
  readback?: string | null;
  errors: string[];
  templatePatch?: Record<string, unknown> | null;
};

const IDLE: StrategyRdRuleStatus = { state: "idle", errors: [] };
const COMPILE_DEBOUNCE_MS = 300;

/** Debounced `POST /rules/compile` for one field. */
function useStrategyRdRuleCompile(
  role: StrategyRdRuleRole,
  rule: unknown,
  composer: StrategyRdRuleComposer | null,
  onStatusChange?: (status: StrategyRdRuleStatus) => void
): StrategyRdRuleStatus {
  const [status, setStatus] = useState<StrategyRdRuleStatus>(IDLE);
  const ruleKey = useMemo(() => (rule == null ? "" : JSON.stringify(rule)), [rule]);
  const composerKey = useMemo(() => JSON.stringify(composer ?? {}), [composer]);
  const onStatusRef = useRef(onStatusChange);
  onStatusRef.current = onStatusChange;

  useEffect(() => {
    const publish = (next: StrategyRdRuleStatus) => {
      setStatus(next);
      onStatusRef.current?.(next);
    };
    if (!ruleKey || !composer) {
      publish(IDLE);
      return;
    }
    publish({ state: "checking", errors: [] });
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      compileStrategyRdRule({ composer, role, rule: JSON.parse(ruleKey) }, controller.signal)
        .then((res) =>
          publish(
            res.ok
              ? { state: "ok", readback: res.readback, errors: [], templatePatch: res.template_patch }
              : {
                  state: "error",
                  errors: res.errors.length ? res.errors : ["This rule doesn't fit the recipe."],
                }
          )
        )
        .catch((err: unknown) => {
          if (err instanceof DOMException && err.name === "AbortError") return;
          publish({
            state: "error",
            errors: ["Couldn't check this rule. Edit it or try again in a moment."],
          });
        });
    }, COMPILE_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
    // composerKey stands in for composer.
  }, [role, ruleKey, composerKey]);

  return status;
}

// ---------------------------------------------------------------------------
// Small inputs
// ---------------------------------------------------------------------------

export function SmallNumber({
  value,
  onCommit,
  min,
  max,
  step = "any",
  width = "w-16",
  ariaLabel,
}: {
  value: number;
  onCommit: (next: number) => void;
  min?: number;
  max?: number;
  step?: string | number;
  width?: string;
  ariaLabel: string;
}): JSX.Element {
  const [text, setText] = useState(String(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(String(value));
  }, [focused, value]);
  const clamp = (n: number) =>
    Math.min(max ?? Number.POSITIVE_INFINITY, Math.max(min ?? Number.NEGATIVE_INFINITY, n));
  return (
    <Input
      type="number"
      aria-label={ariaLabel}
      className={`h-8 ${width} px-2`}
      min={min}
      max={max}
      step={step}
      value={text}
      onFocus={() => setFocused(true)}
      onChange={(e) => {
        setText(e.target.value);
        const n = Number(e.target.value);
        if (e.target.value.trim() !== "" && Number.isFinite(n)) onCommit(clamp(n));
      }}
      onBlur={() => {
        setFocused(false);
        setText(String(value));
      }}
    />
  );
}

function Pick({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (next: string) => void;
  ariaLabel: string;
}): JSX.Element {
  // Keep an unknown current value visible (e.g. a slot the recipe dropped) — compile flags it.
  const shown = options.some((o) => o.value === value)
    ? options
    : [...options, { value, label: value || "N/A" }];
  return (
    <span aria-label={ariaLabel} className="inline-flex">
      <FieldSelectFit value={value} onValueChange={onChange} options={shown} />
    </span>
  );
}

function Word({ children }: { children: React.ReactNode }): JSX.Element {
  return <span className="text-sm text-muted-foreground">{children}</span>;
}

// ---------------------------------------------------------------------------
// Price builder
// ---------------------------------------------------------------------------

type BuilderCtx = { vocabulary: StrategyRdRuleVocabulary | null; role: StrategyRdRuleRole };

function sourceOptions(ctx: BuilderCtx): Array<{ value: string; label: string }> {
  return strategyRdRuleSourcesForRole(ctx.vocabulary, ctx.role).map((key) => ({
    value: key,
    label: strategyRdRuleSourceLabel(key, ctx.vocabulary),
  }));
}

function zoneOptions(ctx: BuilderCtx): Array<{ value: string; label: string }> {
  return strategyRdRuleZoneSources(ctx.vocabulary, ctx.role).map((key) => ({
    value: key,
    label: strategyRdRuleSourceLabel(key, ctx.vocabulary),
  }));
}

/** Price values, plus "a level inside the zone" (`level_in`) where the source has a zone. */
function valueOptions(ctx: BuilderCtx, of: string): Array<{ value: string; label: string }> {
  return strategyRdRulePriceValues(ctx.vocabulary, of, ctx.role).map((v) => ({
    value: v,
    label: v === "zone" ? "level inside the zone" : strategyRdRuleValueLabel(v),
  }));
}

function unitOptions(ctx: BuilderCtx): Array<{ value: string; label: string }> {
  return strategyRdRuleDistUnits(ctx.vocabulary, ctx.role).map((u) => ({
    value: u,
    label: STRATEGY_RD_DIST_UNIT_LABELS[u],
  }));
}

const DIR_OPTIONS = (Object.keys(STRATEGY_RD_OFFSET_DIR_LABELS) as StrategyRdRuleOffsetDir[]).map(
  (d) => ({ value: d, label: STRATEGY_RD_OFFSET_DIR_LABELS[d] })
);

function withSource(ctx: BuilderCtx, form: StrategyRdPriceForm, of: string): StrategyRdPriceForm {
  const values = strategyRdRulePriceValues(ctx.vocabulary, of, ctx.role);
  const value = values.includes(form.value) ? form.value : values.find((v) => v !== "zone") || "level";
  return { ...form, of, value, levelPct: value === "zone" ? form.levelPct ?? 0.5 : null };
}

function PriceFormRow({
  ctx,
  form,
  onChange,
  allowOffset = true,
}: {
  ctx: BuilderCtx;
  form: StrategyRdPriceForm;
  onChange: (next: StrategyRdPriceForm) => void;
  allowOffset?: boolean;
}): JSX.Element {
  const offset = form.offset;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {offset ? (
        <>
          <SmallNumber
            ariaLabel="Distance"
            value={offset.amount}
            min={0}
            onCommit={(amount) => onChange({ ...form, offset: { ...offset, amount } })}
          />
          <Pick
            ariaLabel="Distance unit"
            value={offset.unit}
            options={unitOptions(ctx)}
            onChange={(unit) =>
              onChange({ ...form, offset: { ...offset, unit: unit as StrategyRdRuleDistanceUnit } })
            }
          />
          <Pick
            ariaLabel="Direction"
            value={offset.dir}
            options={DIR_OPTIONS}
            onChange={(dir) =>
              onChange({ ...form, offset: { ...offset, dir: dir as StrategyRdRuleOffsetDir } })
            }
          />
        </>
      ) : null}
      {form.value === "zone" ? (
        <>
          <SmallNumber
            ariaLabel="Percent into the zone"
            value={Math.round((form.levelPct ?? 0.5) * 100)}
            min={0}
            max={100}
            width="w-14"
            onCommit={(pct) => onChange({ ...form, levelPct: pct / 100 })}
          />
          <Word>% into</Word>
        </>
      ) : null}
      <Pick
        ariaLabel="Price source"
        value={form.of}
        options={sourceOptions(ctx)}
        onChange={(of) => onChange(withSource(ctx, form, of))}
      />
      <Pick
        ariaLabel="Price value"
        value={form.value}
        options={valueOptions(ctx, form.of)}
        onChange={(value) =>
          onChange({ ...form, value, levelPct: value === "zone" ? form.levelPct ?? 0.5 : null })
        }
      />
      {allowOffset ? (
        offset ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label="Remove the distance"
            onClick={() => onChange({ ...form, offset: null })}
          >
            <X className="size-3.5" aria-hidden />
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline-brand"
            size="sm"
            data-font="primary"
            className={cn(strategyRdBrandActionButtonClassName, "w-fit")}
            onClick={() =>
              onChange({
                ...form,
                offset: {
                  dir: ctx.role === "target" ? "toward" : "beyond",
                  unit: "atr",
                  amount: 0.1,
                },
              })
            }
          >
            <Plus className="size-3.5" aria-hidden />
            <span className="normal-case tracking-normal">Add distance</span>
          </Button>
        )
      ) : null}
    </span>
  );
}

const COMBINE_OPTIONS = (Object.keys(STRATEGY_RD_COMBINE_LABELS) as StrategyRdRuleCombineOp[]).map(
  (op) => ({ value: op, label: STRATEGY_RD_COMBINE_LABELS[op] })
);

function PriceSentenceBuilder({
  ctx,
  sentence,
  onChange,
}: {
  ctx: BuilderCtx;
  sentence: StrategyRdPriceSentence;
  onChange: (next: StrategyRdPriceSentence) => void;
}): JSX.Element {
  const [first, second] = sentence.parts;
  const setPart = (i: number, next: StrategyRdPriceForm) =>
    onChange({ ...sentence, parts: sentence.parts.map((p, j) => (j === i ? next : p)) });
  return (
    <div className="flex flex-col gap-1.5">
      {second ? (
        <span className="inline-flex items-center gap-1.5">
          <Pick
            ariaLabel="Combine prices"
            value={sentence.combine || "further"}
            options={COMBINE_OPTIONS}
            onChange={(combine) =>
              onChange({ ...sentence, combine: combine as StrategyRdRuleCombineOp })
            }
          />
        </span>
      ) : null}
      {first ? <PriceFormRow ctx={ctx} form={first} onChange={(n) => setPart(0, n)} /> : null}
      {second ? (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <Word>and</Word>
          <PriceFormRow ctx={ctx} form={second} onChange={(n) => setPart(1, n)} />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label="Remove the second price"
            onClick={() => onChange({ parts: [first] })}
          >
            <X className="size-3.5" aria-hidden />
          </Button>
        </span>
      ) : (
        <Button
          type="button"
          variant="outline-brand"
          size="sm"
          data-font="primary"
        className={cn(strategyRdBrandActionButtonClassName, "w-fit")}
          onClick={() =>
            onChange({
              combine: ctx.role === "target" ? "nearer" : "further",
              parts: [first, strategyRdDefaultPriceForm(ctx.vocabulary, ctx.role)],
            })
          }
        >
          <Plus className="size-3.5" aria-hidden />
          <span className="normal-case tracking-normal">Combine levels</span>
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Condition builder
// ---------------------------------------------------------------------------

type TestKind = StrategyRdCondTest["t"];

const TEST_KIND_OPTIONS: Array<{ value: TestKind; label: string }> = [
  { value: "price", label: "the close / price" },
  { value: "time", label: "the time" },
  { value: "compare", label: "a size" },
  { value: "overlaps", label: "two zones" },
  { value: "inside", label: "a price in a zone" },
];

function defaultTest(ctx: BuilderCtx, t: TestKind): StrategyRdCondTest {
  const form = strategyRdDefaultPriceForm(ctx.vocabulary, ctx.role);
  const price: StrategyRdPriceForm = { of: form.of, value: form.value };
  const zones = strategyRdRuleZoneSources(ctx.vocabulary, ctx.role);
  const zone = zones.includes("setup") ? "setup" : zones[0] || "setup";
  if (t === "price") return { t, op: "closes_past", price };
  if (t === "time") return { t, from: "08:00", to: "11:00", tz: "Europe/London" };
  if (t === "compare") {
    return { t, op: "gte", left: { k: "height", of: zone }, right: { k: "dist", unit: "atr", amount: 0.5 } };
  }
  if (t === "overlaps") return { t, a: zone, b: zones.find((z) => z !== zone) || zone };
  return { t: "inside", price, zone };
}

type OperandKind = StrategyRdRuleOperand["k"];

const OPERAND_OPTIONS: Array<{ value: OperandKind; label: string }> = [
  { value: "height", label: "the height of" },
  { value: "span", label: "the distance between" },
  { value: "dist", label: "a distance" },
  { value: "price", label: "a price" },
];

function defaultOperand(ctx: BuilderCtx, k: OperandKind): StrategyRdRuleOperand {
  const zones = strategyRdRuleZoneSources(ctx.vocabulary, ctx.role);
  if (k === "height") return { k, of: zones.includes("setup") ? "setup" : zones[0] || "setup" };
  if (k === "span") return { k, a: { of: "signal", value: "open" }, b: { of: "signal", value: "close" } };
  if (k === "dist") return { k, unit: "atr", amount: 1 };
  const form = strategyRdDefaultPriceForm(ctx.vocabulary, ctx.role);
  return { k, form: { of: form.of, value: form.value } };
}

function RefPick({
  ctx,
  value,
  onChange,
}: {
  ctx: BuilderCtx;
  value: { of: string; value: string };
  onChange: (next: { of: string; value: string }) => void;
}): JSX.Element {
  const values = valueOptions(ctx, value.of).filter((o) => o.value !== "zone");
  return (
    <span className="inline-flex items-center gap-1.5">
      <Pick
        ariaLabel="Source"
        value={value.of}
        options={sourceOptions(ctx)}
        onChange={(of) => {
          const next = strategyRdRulePriceValues(ctx.vocabulary, of, ctx.role).filter((v) => v !== "zone");
          onChange({ of, value: next.includes(value.value) ? value.value : next[0] || "level" });
        }}
      />
      <Pick ariaLabel="Value" value={value.value} options={values} onChange={(v) => onChange({ ...value, value: v })} />
    </span>
  );
}

function OperandEditor({
  ctx,
  operand,
  onChange,
  kinds,
}: {
  ctx: BuilderCtx;
  operand: StrategyRdRuleOperand;
  onChange: (next: StrategyRdRuleOperand) => void;
  kinds: OperandKind[];
}): JSX.Element {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <Pick
        ariaLabel="Measure"
        value={operand.k}
        options={OPERAND_OPTIONS.filter((o) => kinds.includes(o.value))}
        onChange={(k) => onChange(defaultOperand(ctx, k as OperandKind))}
      />
      {operand.k === "height" ? (
        <Pick
          ariaLabel="Zone"
          value={operand.of}
          options={zoneOptions(ctx)}
          onChange={(of) => onChange({ ...operand, of })}
        />
      ) : operand.k === "span" ? (
        <>
          <RefPick ctx={ctx} value={operand.a} onChange={(a) => onChange({ ...operand, a })} />
          <Word>and</Word>
          <RefPick ctx={ctx} value={operand.b} onChange={(b) => onChange({ ...operand, b })} />
        </>
      ) : operand.k === "dist" ? (
        <>
          <SmallNumber
            ariaLabel="Distance"
            value={operand.amount}
            min={0}
            onCommit={(amount) => onChange({ ...operand, amount })}
          />
          <Pick
            ariaLabel="Distance unit"
            value={operand.unit}
            options={unitOptions(ctx)}
            onChange={(unit) => onChange({ ...operand, unit: unit as StrategyRdRuleDistanceUnit })}
          />
        </>
      ) : (
        <PriceFormRow ctx={ctx} form={operand.form} onChange={(form) => onChange({ ...operand, form })} />
      )}
    </span>
  );
}

function CondTestEditor({
  ctx,
  test,
  onChange,
}: {
  ctx: BuilderCtx;
  test: StrategyRdCondTest;
  onChange: (next: StrategyRdCondTest) => void;
}): JSX.Element | null {
  if (test.t === "price") {
    return (
      <>
        <Pick
          ariaLabel="Price test"
          value={test.op}
          options={STRATEGY_RD_PRICE_TESTS.map((op) => ({ value: op, label: STRATEGY_RD_PRICE_TEST_LABELS[op] }))}
          onChange={(op) => onChange({ ...test, op: op as typeof test.op })}
        />
        <PriceFormRow ctx={ctx} form={test.price} onChange={(price) => onChange({ ...test, price })} />
      </>
    );
  }
  if (test.t === "time") {
    return (
      <>
        <Word>is between</Word>
        <Input
          aria-label="From (HH:MM)"
          className="h-8 w-20 px-2"
          value={test.from}
          placeholder="08:00"
          onChange={(e) => onChange({ ...test, from: e.target.value })}
        />
        <Word>and</Word>
        <Input
          aria-label="To (HH:MM)"
          className="h-8 w-20 px-2"
          value={test.to}
          placeholder="11:00"
          onChange={(e) => onChange({ ...test, to: e.target.value })}
        />
        <Pick
          ariaLabel="Time zone"
          value={test.tz}
          options={STRATEGY_RD_RULE_TIME_ZONES.map((tz) => ({ value: tz, label: tz }))}
          onChange={(tz) => onChange({ ...test, tz })}
        />
      </>
    );
  }
  if (test.t === "compare") {
    return (
      <>
        <OperandEditor
          ctx={ctx}
          operand={test.left}
          kinds={["height", "span", "price"]}
          onChange={(left) => onChange({ ...test, left })}
        />
        <Pick
          ariaLabel="Compare"
          value={test.op}
          options={STRATEGY_RD_COMPARE_OPS.map((op) => ({ value: op, label: STRATEGY_RD_COMPARE_LABELS[op] }))}
          onChange={(op) => onChange({ ...test, op: op as typeof test.op })}
        />
        <OperandEditor
          ctx={ctx}
          operand={test.right}
          kinds={["dist", "height", "span", "price"]}
          onChange={(right) => onChange({ ...test, right })}
        />
      </>
    );
  }
  if (test.t === "overlaps") {
    return (
      <>
        <Pick ariaLabel="Zone" value={test.a} options={zoneOptions(ctx)} onChange={(a) => onChange({ ...test, a })} />
        <Word>overlaps</Word>
        <Pick ariaLabel="Zone" value={test.b} options={zoneOptions(ctx)} onChange={(b) => onChange({ ...test, b })} />
      </>
    );
  }
  return (
    <>
      <PriceFormRow ctx={ctx} form={test.price} allowOffset={false} onChange={(price) => onChange({ ...test, price })} />
      <Word>is inside</Word>
      <Pick ariaLabel="Zone" value={test.zone} options={zoneOptions(ctx)} onChange={(zone) => onChange({ ...test, zone })} />
    </>
  );
}

const REPEAT_OPTIONS = [
  { value: "once", label: "on the bar" },
  { value: "consecutive", label: "on bars in a row:" },
  { value: "count", label: "on bars since the setup:" },
];

function CondItemRow({
  ctx,
  item,
  onChange,
  onRemove,
}: {
  ctx: BuilderCtx;
  item: StrategyRdCondItem;
  onChange: (next: StrategyRdCondItem) => void;
  onRemove?: () => void;
}): JSX.Element {
  // Filters are checked once on the ready bar — nothing to count yet.
  const canRepeat = ctx.role !== "filter";
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-strategy-rd-rule-condition="">
      <label className="inline-flex items-center gap-1 text-sm text-muted-foreground">
        <Checkbox
          checked={item.negate === true}
          onCheckedChange={(checked) => onChange({ ...item, negate: checked === true })}
          aria-label="Not"
        />
        not
      </label>
      <Pick
        ariaLabel="Condition on"
        value={item.test.t}
        options={TEST_KIND_OPTIONS}
        onChange={(t) => onChange({ ...item, test: defaultTest(ctx, t as TestKind) })}
      />
      <CondTestEditor ctx={ctx} test={item.test} onChange={(test) => onChange({ ...item, test })} />
      {canRepeat ? (
        <>
          <Pick
            ariaLabel="How often"
            value={item.repeat?.kind || "once"}
            options={REPEAT_OPTIONS}
            onChange={(kind) =>
              onChange({
                ...item,
                repeat:
                  kind === "once"
                    ? null
                    : { kind: kind as "consecutive" | "count", n: item.repeat?.n ?? 2 },
              })
            }
          />
          {item.repeat ? (
            <SmallNumber
              ariaLabel="Bars"
              value={item.repeat.n}
              min={1}
              max={500}
              step={1}
              width="w-14"
              onCommit={(n) => onChange({ ...item, repeat: { ...item.repeat!, n: Math.floor(n) } })}
            />
          ) : null}
        </>
      ) : null}
      {onRemove ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label="Remove condition"
          onClick={onRemove}
        >
          <X className="size-3.5" aria-hidden />
        </Button>
      ) : null}
    </div>
  );
}

function CondSentenceBuilder({
  ctx,
  sentence,
  onChange,
}: {
  ctx: BuilderCtx;
  sentence: StrategyRdCondSentence;
  onChange: (next: StrategyRdCondSentence) => void;
}): JSX.Element {
  const many = sentence.items.length > 1;
  return (
    <div className="flex flex-col gap-1.5">
      {many ? (
        <span className="inline-flex items-center gap-1.5">
          <Pick
            ariaLabel="Join conditions"
            value={sentence.join}
            options={[
              { value: "and", label: "all of these" },
              { value: "or", label: "any of these" },
            ]}
            onChange={(join) => onChange({ ...sentence, join: join as "and" | "or" })}
          />
        </span>
      ) : null}
      {sentence.items.map((item, i) => (
        <CondItemRow
          key={i}
          ctx={ctx}
          item={item}
          onChange={(next) =>
            onChange({ ...sentence, items: sentence.items.map((it, j) => (j === i ? next : it)) })
          }
          onRemove={
            many
              ? () => onChange({ ...sentence, items: sentence.items.filter((_, j) => j !== i) })
              : undefined
          }
        />
      ))}
      <Button
        type="button"
        variant="outline-brand"
        size="sm"
        data-font="primary"
        className={cn(strategyRdBrandActionButtonClassName, "w-fit")}
        onClick={() =>
          onChange({
            ...sentence,
            items: [...sentence.items, strategyRdDefaultCondItem(ctx.vocabulary, ctx.role)],
          })
        }
      >
        <Plus className="size-3.5" aria-hidden />
        <span className="normal-case tracking-normal">Add condition</span>
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shell: examples, readback / errors (no JSON — rules are built as sentences)
// ---------------------------------------------------------------------------

export function StrategyRdRuleStatusLine({ status }: { status: StrategyRdRuleStatus }): JSX.Element | null {
  if (status.state === "checking") {
    return (
      <p className={`${strategyRdMetaClassName} inline-flex items-center gap-1.5 text-[11px]`}>
        <Loader2 className="size-3 animate-spin" aria-hidden /> Checking…
      </p>
    );
  }
  if (status.state === "error") {
    return (
      <ul className="flex flex-col gap-0.5 text-xs text-destructive" data-strategy-rd-rule-errors="">
        {status.errors.map((e, i) => (
          <li key={i}>{e}</li>
        ))}
      </ul>
    );
  }
  if (status.state === "ok" && status.readback) {
    return (
      <p className={`${strategyRdMetaClassName} text-[11px]`} data-strategy-rd-rule-readback="">
        {status.readback}
      </p>
    );
  }
  return null;
}

function RuleShell({
  role,
  vocabulary,
  canBuild,
  onExample,
  onReset,
  status,
  children,
}: {
  role: StrategyRdRuleRole;
  vocabulary: StrategyRdRuleVocabulary | null;
  /** False when the current rule is more detailed than the sentence builder can show. */
  canBuild: boolean;
  onExample: (rule: StrategyRdRuleExpr) => void;
  /** Replace the rule with a simple one the sentence builder can edit. */
  onReset: () => void;
  status: StrategyRdRuleStatus;
  children: React.ReactNode;
}): JSX.Element {
  const examples = strategyRdRuleExamples(vocabulary, role);
  return (
    <div
      className="flex flex-col gap-2 rounded-[6px] border border-border/70 p-3"
      data-strategy-rd-rule-editor={role}
    >
      {examples.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <FieldSelectFit
            value=""
            emptyValueShowsPlaceholder
            placeholder="Start from an example"
            options={examples.map((ex, i) => ({ value: String(i), label: ex.label }))}
            onValueChange={(i) => {
              const ex = examples[Number(i)];
              if (ex) onExample(ex.rule);
            }}
          />
        </div>
      ) : null}
      {canBuild ? (
        children
      ) : (
        // A rule TES wrote in more detail than the builder shows: read it in words, change it in words.
        <div className="flex flex-col gap-1.5 rounded-[6px] bg-muted/50 px-3 py-2.5" data-strategy-rd-rule-plain="">
          <span className="font-[family-name:var(--font-secondary)] text-[10px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
            Custom rule
          </span>
          <p className="font-[family-name:var(--font-secondary)] text-xs leading-relaxed text-gray-500 dark:text-white">
            {status.state === "ok" && status.readback
              ? status.readback
              : status.state === "error"
                ? "This rule does not pass the check. See below."
                : "Reading the rule…"}
          </p>
          <p className={`${strategyRdMetaClassName} text-[11px]`}>
            More detailed than the sentence builder can show. To change it, ask TES in plain words, pick an
            example above, or start over with a simple rule you can build here.
          </p>
          <div>
            <Button type="button" variant="outline-brand" size="xs" data-font="primary" onClick={onReset}>
              <span className="normal-case tracking-normal">Start over</span>
            </Button>
          </div>
        </div>
      )}
      {vocabulary && vocabulary.not_usable.length > 0 ? (
        <p className={`${strategyRdMetaClassName} text-[11px] opacity-70`} data-strategy-rd-rule-not-usable="">
          No price to read from{" "}
          {vocabulary.not_usable.map((k) => strategyRdRuleSourceLabel(k, vocabulary)).join(", ")}. They
          are true / false filters.
        </p>
      ) : null}
      {!canBuild && status.state === "ok" ? null : <StrategyRdRuleStatusLine status={status} />}
    </div>
  );
}

export type StrategyRdRuleEditorBaseProps = {
  vocabulary: StrategyRdRuleVocabulary | null;
  /** The recipe the rule is checked against (null while it has no setup). */
  composer: StrategyRdRuleComposer | null;
  onStatusChange?: (status: StrategyRdRuleStatus) => void;
};

// ---------------------------------------------------------------------------
// Stop / target
// ---------------------------------------------------------------------------

/** Stop or target price rule: "[distance] beyond [source] [value]" or "[n] R from entry". */
export function StrategyRdPriceRuleEditor({
  role,
  rule,
  onChange,
  vocabulary,
  composer,
  onStatusChange,
}: StrategyRdRuleEditorBaseProps & {
  role: "stop" | "target";
  rule: StrategyRdRuleExpr;
  onChange: (next: StrategyRdRuleExpr) => void;
}): JSX.Element {
  const sentence = useMemo(() => strategyRdPriceSentenceFromJson(rule), [rule]);
  const status = useStrategyRdRuleCompile(role, rule, composer, onStatusChange);
  const ctx = { vocabulary, role };
  return (
    <RuleShell
      role={role}
      vocabulary={vocabulary}
      canBuild={sentence != null}
      onExample={onChange}
      onReset={() => onChange(strategyRdDefaultPriceRule(vocabulary, role))}
      status={status}
    >
      {sentence != null ? (
        <PriceSentenceBuilder
          ctx={ctx}
          sentence={sentence}
          onChange={(next) => onChange(strategyRdPriceSentenceToJson(next))}
        />
      ) : null}
    </RuleShell>
  );
}

export function strategyRdDefaultPriceRule(
  vocabulary: StrategyRdRuleVocabulary | null,
  role: "stop" | "target"
): StrategyRdRuleExpr {
  return strategyRdPriceSentenceToJson({ parts: [strategyRdDefaultPriceForm(vocabulary, role)] });
}

// ---------------------------------------------------------------------------
// Filter
// ---------------------------------------------------------------------------

/** "Only take the setup if [condition]". */
export function StrategyRdFilterRuleEditor({
  rule,
  onChange,
  vocabulary,
  composer,
  onStatusChange,
}: StrategyRdRuleEditorBaseProps & {
  rule: StrategyRdRuleExpr;
  onChange: (next: StrategyRdRuleExpr) => void;
}): JSX.Element {
  const sentence = useMemo(() => strategyRdCondSentenceFromJson(rule), [rule]);
  const status = useStrategyRdRuleCompile("filter", rule, composer, onStatusChange);
  const ctx: BuilderCtx = { vocabulary, role: "filter" };
  return (
    <RuleShell
      role="filter"
      vocabulary={vocabulary}
      canBuild={sentence != null}
      onExample={onChange}
      onReset={() => onChange(strategyRdDefaultFilterRule(vocabulary))}
      status={status}
    >
      {sentence == null ? null : (
        <div className="flex flex-col gap-1.5">
          <Word>Only take the setup if</Word>
          <CondSentenceBuilder
            ctx={ctx}
            sentence={sentence}
            onChange={(next) => onChange(strategyRdCondSentenceToJson(next))}
          />
        </div>
      )}
    </RuleShell>
  );
}

export function strategyRdDefaultFilterRule(vocabulary: StrategyRdRuleVocabulary | null): StrategyRdRuleExpr {
  return strategyRdCondSentenceToJson({
    join: "and",
    items: [strategyRdDefaultCondItem(vocabulary, "filter")],
  });
}

// ---------------------------------------------------------------------------
// Entry
// ---------------------------------------------------------------------------

const ORDER_OPTIONS = [
  { value: "market", label: "Market order" },
  { value: "limit", label: "Limit order" },
  { value: "stop", label: "Stop order" },
];

/** "[Market / Limit / Stop] order at [price] when [condition], cancel if [condition]". */
export function StrategyRdEntryRuleEditor({
  rule,
  onChange,
  vocabulary,
  composer,
  onStatusChange,
}: StrategyRdRuleEditorBaseProps & {
  rule: StrategyRdEntryRuleSpec;
  onChange: (next: StrategyRdEntryRuleSpec) => void;
}): JSX.Element {
  const price = useMemo(
    () => (rule.price ? strategyRdPriceSentenceFromJson(rule.price) : null),
    [rule.price]
  );
  const when = useMemo(() => (rule.when ? strategyRdCondSentenceFromJson(rule.when) : null), [rule.when]);
  const cancel = useMemo(
    () => (rule.cancel_if ? strategyRdCondSentenceFromJson(rule.cancel_if) : null),
    [rule.cancel_if]
  );
  const buildable =
    (rule.price == null || price != null) &&
    (rule.when == null || when != null) &&
    (rule.cancel_if == null || cancel != null);
  const status = useStrategyRdRuleCompile("entry", rule, composer, onStatusChange);
  const ctx: BuilderCtx = { vocabulary, role: "entry" };
  const market = rule.order === "market";
  const newCond = () =>
    strategyRdCondSentenceToJson({ join: "and", items: [strategyRdDefaultCondItem(vocabulary, "entry")] });

  return (
    <RuleShell
      role="entry"
      vocabulary={vocabulary}
      canBuild={buildable}
      onExample={(ex) => onChange(ex as unknown as StrategyRdEntryRuleSpec)}
      onReset={() => onChange(strategyRdDefaultEntryRule(vocabulary))}
      status={status}
    >
      {!buildable ? null : (
        <div className="flex flex-col gap-2">
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <Pick
              ariaLabel="Order type"
              value={rule.order}
              options={ORDER_OPTIONS}
              onChange={(order) => {
                const o = order as StrategyRdEntryRuleSpec["order"];
                if (o === "market") {
                  // Market fills at the close where `when` holds; it has no price.
                  const { price: _p, ...rest } = rule;
                  onChange({ ...rest, order: o, when: rule.when ?? newCond() });
                } else {
                  onChange({
                    ...rule,
                    order: o,
                    price:
                      rule.price ??
                      strategyRdPriceSentenceToJson({
                        parts: [{ ...strategyRdDefaultPriceForm(vocabulary, "entry"), value: "zone", levelPct: 0.5, offset: null }],
                      }),
                  });
                }
              }}
            />
            {market ? <Word>at the close when</Word> : <Word>at</Word>}
          </span>
          {!market && price ? (
            <PriceSentenceBuilder
              ctx={ctx}
              sentence={price}
              onChange={(next) => onChange({ ...rule, price: strategyRdPriceSentenceToJson(next) })}
            />
          ) : null}
          {market ? (
            when ? (
              <CondSentenceBuilder
                ctx={ctx}
                sentence={when}
                onChange={(next) => onChange({ ...rule, when: strategyRdCondSentenceToJson(next) })}
              />
            ) : null
          ) : (
            <div className="flex flex-col gap-1.5">
              <span className="inline-flex items-center gap-1.5">
                <Word>placed</Word>
                <Pick
                  ariaLabel="When to place the order"
                  value={rule.when ? "when" : "setup"}
                  options={[
                    { value: "setup", label: "at the setup" },
                    { value: "when", label: "when…" },
                  ]}
                  onChange={(v) => {
                    if (v === "setup") {
                      const { when: _w, ...rest } = rule;
                      onChange(rest as StrategyRdEntryRuleSpec);
                    } else onChange({ ...rule, when: newCond() });
                  }}
                />
              </span>
              {when ? (
                <CondSentenceBuilder
                  ctx={ctx}
                  sentence={when}
                  onChange={(next) => onChange({ ...rule, when: strategyRdCondSentenceToJson(next) })}
                />
              ) : null}
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <span className="inline-flex items-center gap-1.5">
              <Word>cancel</Word>
              <Pick
                ariaLabel="Cancel condition"
                value={rule.cancel_if ? "if" : "never"}
                options={[
                  { value: "never", label: "never (Max wait ends it)" },
                  { value: "if", label: "if…" },
                ]}
                onChange={(v) => {
                  if (v === "never") {
                    const { cancel_if: _c, ...rest } = rule;
                    onChange(rest as StrategyRdEntryRuleSpec);
                  } else onChange({ ...rule, cancel_if: newCond() });
                }}
              />
            </span>
            {cancel ? (
              <CondSentenceBuilder
                ctx={ctx}
                sentence={cancel}
                onChange={(next) => onChange({ ...rule, cancel_if: strategyRdCondSentenceToJson(next) })}
              />
            ) : null}
          </div>
        </div>
      )}
    </RuleShell>
  );
}

export function strategyRdDefaultEntryRule(vocabulary: StrategyRdRuleVocabulary | null): StrategyRdEntryRuleSpec {
  return {
    order: "market",
    when: strategyRdCondSentenceToJson({
      join: "and",
      items: [{ ...strategyRdDefaultCondItem(vocabulary, "entry"), repeat: { kind: "consecutive", n: 2 } }],
    }),
  };
}
